import {
  type AdminQuoteDraft,
  defaultQuoteValidUntil,
  type QuoteDestination,
  type QuoteDraftInput,
  type QuoteLineKind,
  quoteDraftInputSchema,
  quoteDraftLineSchema,
  SHIPPING_FEE_MAX,
  shippingLineName,
  splitQuoteAmounts,
} from '@repo/api-contracts';

/** 편집 중인 '상품' 묶음 줄(kind PRODUCT) — 숫자 칸도 입력 문자열 그대로 둔다(정수 검사는 저장 때). */
export interface DraftLineForm {
  readonly key: string;
  readonly name: string;
  readonly quantity: string;
  readonly unitPrice: string;
}

/** '배송비' 묶음 한 행(SUPER-86 §6-2) — 배송지마다 정확히 하나, 수량은 늘 1. */
export interface ShippingLineForm {
  readonly destinationId: string;
  /** 저장된 배송비 줄 이름, 없으면 shippingLineName(...) 로 만든 이름 — 화면에서 고치지 않는다. */
  readonly name: string;
  /** 입력 문자열 그대로(정수 검사는 저장 때). */
  readonly unitPrice: string;
}

export interface DraftForm {
  /** '상품' 묶음(kind PRODUCT) — 추가·삭제·정렬. */
  readonly lines: readonly DraftLineForm[];
  /** '배송비' 묶음 — 배송지 sortOrder 순, 배송지마다 정확히 하나. */
  readonly shippingLines: readonly ShippingLineForm[];
  readonly productionStartNote: string;
  readonly deliveryNote: string;
  readonly deliveryMethodNote: string;
  /** YYYY-MM-DD, 비우면 null 로 저장한다. */
  readonly validUntil: string;
  readonly specialTerms: string;
}

export interface DraftLineError {
  readonly name?: string;
  readonly quantity?: string;
  readonly unitPrice?: string;
}

export type DraftFormParseResult =
  | { readonly ok: true; readonly input: QuoteDraftInput }
  | {
      readonly ok: false;
      readonly lineErrors: Readonly<Record<string, DraftLineError>>;
      /** 배송지 id → 오류 문구. */
      readonly shippingErrors: Readonly<Record<string, string>>;
      readonly formError: string | null;
    };

export interface DraftAmountPreview {
  /** 상품 줄 순서대로의 금액. 아직 정수가 아닌 줄은 null. */
  readonly lineAmounts: readonly (number | null)[];
  /** 배송비 줄 순서대로의 금액. 0 이상 정수가 아니면 null. */
  readonly shippingAmounts: readonly (number | null)[];
  readonly productAmount: number;
  readonly shippingAmount: number;
  readonly totalAmount: number;
  readonly supplyAmount: number;
  readonly vatAmount: number;
  /** 모든 줄이 계산에 들어갔는지. */
  readonly isComplete: boolean;
}

const LINE_NAME_MESSAGE = '품목 이름을 1~100자로 입력해 주세요.';
const QUANTITY_MESSAGE = '수량은 1 이상의 정수로 입력해 주세요.';
const UNIT_PRICE_MESSAGE = '단가는 원 단위 정수로 입력해 주세요(할인은 음수).';
const FORM_MESSAGE =
  '입력값이 올바르지 않습니다. 유효기한(만료일, YYYY-MM-DD)과 금액 범위를 확인해 주세요.';
const VALID_UNTIL_PAST_MESSAGE = '유효기한(만료일)은 한국 시간 오늘 이후여야 합니다.';
const SHIPPING_FEE_MESSAGE = '배송비는 0 이상의 원 단위 정수로 입력해 주세요.';
/** 배송비 설정과 같은 상한(SHIPPING_FEE_MAX) — 서버 저장·발행 검증(`assertShippingLines`)도 같은 상한을 본다. */
const SHIPPING_FEE_MAX_MESSAGE = `배송비는 ${SHIPPING_FEE_MAX.toLocaleString('ko-KR')}원 이하로 입력해 주세요.`;
const INTEGER_PATTERN = /^-?\d+$/;

const nextLineKey = (() => {
  let sequence = 0;
  return (): string => {
    sequence += 1;
    return `line-${sequence}`;
  };
})();

/** 정수 문자열 → 정수. 빈 값·소수·천 단위 구분자·범위 밖은 null. */
const parseInteger = (text: string): number | null => {
  const trimmed = text.trim();

  if (!INTEGER_PATTERN.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
};

const sortedLines = (draft: AdminQuoteDraft): AdminQuoteDraft['lines'] =>
  [...draft.lines].sort((a, b) => a.sortOrder - b.sortOrder);

/** 서버에 배송비 줄이 저장돼 있는 배송지 id — '저장 안 됨' 표시와 저장 스냅샷의 기준. */
export const savedShippingDestinationIds = (draft: AdminQuoteDraft): ReadonlySet<string> =>
  new Set(
    draft.lines.flatMap((line) =>
      line.kind === 'SHIPPING' && line.destinationId !== null ? [line.destinationId] : [],
    ),
  );

/**
 * 배송비 묶음(SUPER-86 §6-2) — 배송지 sortOrder 순으로 배송지마다 한 행. 저장된 SHIPPING 줄이 있으면 그 이름·금액,
 * 없으면(요청 전 초안·배송비 줄이 빠진 재발행 초안) 지금 설정 기준 추천 배송비(`suggestedShippingFees`)로 채운다.
 * 채운 행은 아직 저장되지 않은 변경이다 — 편집기가 '저장 안 됨' 으로 표시하고 저장 전까지 발행을 잠근다.
 */
const shippingLinesFrom = (
  draft: AdminQuoteDraft,
  destinations: readonly QuoteDestination[],
): readonly ShippingLineForm[] =>
  [...destinations]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((destination) => {
      const saved = draft.lines.find(
        (line) => line.kind === 'SHIPPING' && line.destinationId === destination.id,
      );
      if (saved !== undefined) {
        return {
          destinationId: destination.id,
          name: saved.name,
          unitPrice: String(saved.unitPrice),
        };
      }
      const suggestion = draft.suggestedShippingFees.find(
        (item) => item.destinationId === destination.id,
      );
      return {
        destinationId: destination.id,
        // 지역을 알 수 없는 배송지(region null, 교차 조정 R1)는 이름에 '일반'을 쓴다 — 금액은 추천값이 정한다.
        name: shippingLineName({
          sortOrder: destination.sortOrder,
          region: suggestion?.region ?? destination.region ?? 'STANDARD',
        }),
        unitPrice: suggestion === undefined ? '' : String(suggestion.fee),
      };
    });

/**
 * 서버 초안 → 폼 값. 상품 줄은 sortOrder 순, 배송비 줄은 배송지 순(`shippingLinesFrom`), 빈 조건·특약은 빈 문자열.
 * 만료일이 비어 있으면(요청 때 만든 v1 초안) 편집기를 여는 시점의 기본값(오늘+7일, 서버와 같은 공용
 * `defaultQuoteValidUntil`)을 채운다 — 발행일 기준이라
 * 요청 시점이 아니라 지금 계산한다. 채운 값은 아직 저장되지 않은 변경이다.
 */
export const draftFormFromDraft = (
  draft: AdminQuoteDraft,
  destinations: readonly QuoteDestination[],
  today: string,
): DraftForm => ({
  lines: sortedLines(draft)
    .filter((line) => line.kind === 'PRODUCT')
    .map((line) => ({
      key: nextLineKey(),
      name: line.name,
      quantity: String(line.quantity),
      unitPrice: String(line.unitPrice),
    })),
  shippingLines: shippingLinesFrom(draft, destinations),
  productionStartNote: draft.productionStartNote ?? '',
  deliveryNote: draft.deliveryNote ?? '',
  deliveryMethodNote: draft.deliveryMethodNote ?? '',
  validUntil: draft.validUntil ?? defaultQuoteValidUntil(today),
  specialTerms: draft.specialTerms ?? '',
});

/** [기본값으로] — 추천 배송비가 있는 배송지의 금액을 추천값으로 바꾼다. 이름은 그대로. */
export const resetShippingToSuggested = (
  shippingLines: readonly ShippingLineForm[],
  suggested: AdminQuoteDraft['suggestedShippingFees'],
): readonly ShippingLineForm[] =>
  shippingLines.map((line) => {
    const suggestion = suggested.find((item) => item.destinationId === line.destinationId);
    return suggestion === undefined ? line : { ...line, unitPrice: String(suggestion.fee) };
  });

export const emptyDraftLine = (): DraftLineForm => ({
  key: nextLineKey(),
  name: '',
  quantity: '1',
  unitPrice: '',
});

/** 줄 이름은 계약 스키마(trim 후 UTF-16 1~100자)로 검사한다 — 서버 자동 줄 이름·저장 검증과 같은 기준. */
const isValidLineName = (name: string): boolean =>
  quoteDraftLineSchema.shape.name.safeParse(name).success;

const lineError = (line: DraftLineForm): DraftLineError | null => {
  const quantity = parseInteger(line.quantity);
  const unitPrice = parseInteger(line.unitPrice);
  const error: DraftLineError = {
    ...(isValidLineName(line.name) ? {} : { name: LINE_NAME_MESSAGE }),
    ...(quantity === null || quantity < 1 ? { quantity: QUANTITY_MESSAGE } : {}),
    ...(unitPrice === null ? { unitPrice: UNIT_PRICE_MESSAGE } : {}),
  };

  return Object.keys(error).length === 0 ? null : error;
};

/** 배송비 → 0 ~ SHIPPING_FEE_MAX 정수. 그 밖은 null. */
const parseShippingFee = (text: string): number | null => {
  const value = parseInteger(text);
  return value === null || value < 0 || value > SHIPPING_FEE_MAX ? null : value;
};

const shippingFeeError = (text: string): string | null => {
  const value = parseInteger(text);
  if (value === null || value < 0) return SHIPPING_FEE_MESSAGE;
  return value > SHIPPING_FEE_MAX ? SHIPPING_FEE_MAX_MESSAGE : null;
};

/**
 * 폼 값 → 계약 입력(quoteDraftInputSchema). 줄은 상품 묶음 뒤에 배송비 묶음을 이은 화면 순서가 sortOrder 다(서버가 다시 매긴다).
 * 상품 줄 칸 오류는 줄 키별로, 배송비 오류는 배송지 id 별로, 그 밖의 계약 위반(만료일 형식·금액 범위)과 지난 만료일(`today` 보다 앞, 서버 발행 검증과
 * 같은 기준 — 오늘은 통과)은 폼 오류로 돌려준다.
 */
export interface DraftFormIds {
  readonly quoteRequestId: string;
  readonly versionId: string;
  /** 편집기가 불러왔거나 마지막으로 저장한 초안의 토큰 — 서버가 그 사이 다른 저장을 감지한다. */
  readonly expectedDraftUpdatedAt: string;
}

export const parseDraftForm = (
  form: DraftForm,
  ids: DraftFormIds,
  today: string,
): DraftFormParseResult => {
  const lineErrors = Object.fromEntries(
    form.lines.flatMap((line) => {
      const error = lineError(line);
      return error === null ? [] : [[line.key, error] as const];
    }),
  );

  const shippingErrors = Object.fromEntries(
    form.shippingLines.flatMap((line) => {
      const error = shippingFeeError(line.unitPrice);
      return error === null ? [] : [[line.destinationId, error] as const];
    }),
  );

  if (Object.keys(lineErrors).length > 0 || Object.keys(shippingErrors).length > 0) {
    return { ok: false, lineErrors, shippingErrors, formError: null };
  }

  if (form.validUntil !== '' && form.validUntil < today) {
    return { ok: false, lineErrors: {}, shippingErrors: {}, formError: VALID_UNTIL_PAST_MESSAGE };
  }

  const parsed = quoteDraftInputSchema.safeParse({
    quoteRequestId: ids.quoteRequestId,
    versionId: ids.versionId,
    expectedDraftUpdatedAt: ids.expectedDraftUpdatedAt,
    // admin 은 kind·destinationId 를 늘 명시해 보낸다 — 계약 기본값에 기대지 않는다.
    lines: [
      ...form.lines.map((line) => ({
        name: line.name.trim(),
        quantity: parseInteger(line.quantity),
        unitPrice: parseInteger(line.unitPrice),
        kind: 'PRODUCT' satisfies QuoteLineKind,
        destinationId: null,
      })),
      ...form.shippingLines.map((line) => ({
        name: line.name,
        quantity: 1,
        unitPrice: parseShippingFee(line.unitPrice),
        kind: 'SHIPPING' satisfies QuoteLineKind,
        destinationId: line.destinationId,
      })),
    ].map((line, index) => ({ ...line, sortOrder: index })),
    productionStartNote: form.productionStartNote,
    deliveryNote: form.deliveryNote,
    deliveryMethodNote: form.deliveryMethodNote,
    validUntil: form.validUntil === '' ? null : form.validUntil,
    specialTerms: form.specialTerms,
  });

  if (!parsed.success) {
    return { ok: false, lineErrors: {}, shippingErrors: {}, formError: FORM_MESSAGE };
  }

  return { ok: true, input: parsed.data };
};

/**
 * 다시 불러온 서버 초안이 방금 저장한 입력과 같은 내용인지(초안 id·줄·조건·만료일·특약). 같으면 그 초안의 토큰이 내 저장의
 * 결과이므로 이어 쓰고, 다르면 그 사이 다른 운영자가 저장한 것이라 옛 토큰을 유지해 다음 저장·발행이 충돌로 막히게 한다.
 * 서버는 줄을 sortOrder 순으로 0부터 다시 매기므로 입력(화면 순서 = sortOrder)과 순서대로 비교할 수 있다.
 */
export const draftMatchesInput = (draft: AdminQuoteDraft, input: QuoteDraftInput): boolean => {
  const savedLines = sortedLines(draft);
  const sameLines =
    savedLines.length === input.lines.length &&
    savedLines.every((line, index) => {
      const saved = input.lines[index];
      return (
        saved !== undefined &&
        line.name === saved.name &&
        line.quantity === saved.quantity &&
        line.unitPrice === saved.unitPrice &&
        line.kind === saved.kind &&
        line.destinationId === saved.destinationId
      );
    });

  return (
    draft.versionId === input.versionId &&
    sameLines &&
    draft.productionStartNote === input.productionStartNote &&
    draft.deliveryNote === input.deliveryNote &&
    draft.deliveryMethodNote === input.deliveryMethodNote &&
    draft.validUntil === input.validUntil &&
    draft.specialTerms === input.specialTerms
  );
};

const SERVER_LINE_FIELD = /^lines\.(\d+)\.(name|quantity|unitPrice|destinationId)$/;

/**
 * 서버 줄 칸 오류(`fieldErrors` 의 점 키 `lines.{i}.{칸}`, 교차 조정 R3) → 화면 칸. i 는 보낸 입력 순서(상품 묶음 뒤 배송비 묶음,
 * `parseDraftForm` 과 같은 순서)라 보낸 폼으로 되짚는다. 배송비 행은 칸 하나라 어느 칸 오류든 그 행에, 상품 줄은 같은 칸에
 * 붙인다. 화면에 칸이 없는 오류(상품 줄의 배송지 등)는 붙이지 않는다 — 서버 문구가 폼 오류로 따로 보인다.
 */
export const serverDraftErrors = (
  form: Pick<DraftForm, 'lines' | 'shippingLines'>,
  fieldErrors: Readonly<Record<string, readonly string[]>>,
): {
  readonly lineErrors: Readonly<Record<string, DraftLineError>>;
  readonly shippingErrors: Readonly<Record<string, string>>;
} => {
  const lineErrors: Record<string, DraftLineError> = {};
  const shippingErrors: Record<string, string> = {};

  for (const [key, messages] of Object.entries(fieldErrors)) {
    const match = SERVER_LINE_FIELD.exec(key);
    const message = messages[0];
    if (match === null || message === undefined) continue;
    const index = Number(match[1]);
    const field = match[2];
    const productLine = form.lines[index];

    if (productLine !== undefined) {
      if (field === 'name' || field === 'quantity' || field === 'unitPrice') {
        lineErrors[productLine.key] = { ...lineErrors[productLine.key], [field]: message };
      }
      continue;
    }

    const shippingLine = form.shippingLines[index - form.lines.length];
    if (shippingLine !== undefined) shippingErrors[shippingLine.destinationId] = message;
  }

  return { lineErrors, shippingErrors };
};

/**
 * 표시용 로컬 계산(견적 설계 §6-3, SUPER-86 §7) — 상품 금액 = 수량 × 단가, 배송비 = 행 금액, 상품·배송비 나누기와
 * 공급가액·부가세는 서버·web·worker 와 같은 공용 `splitQuoteAmounts`. 아직 정수가 아닌 줄은 뺀다. 저장값은 서버 응답을 믿는다.
 */
export const previewDraftAmounts = (
  form: Pick<DraftForm, 'lines' | 'shippingLines'>,
): DraftAmountPreview => {
  const lineAmounts = form.lines.map((line) => {
    const quantity = parseInteger(line.quantity);
    const unitPrice = parseInteger(line.unitPrice);
    return quantity === null || quantity < 1 || unitPrice === null ? null : quantity * unitPrice;
  });
  const shippingAmounts = form.shippingLines.map((line) => parseShippingFee(line.unitPrice));
  const amounts = splitQuoteAmounts([
    ...lineAmounts.map((amount) => ({ kind: 'PRODUCT' as const, amount: amount ?? 0 })),
    ...shippingAmounts.map((amount) => ({ kind: 'SHIPPING' as const, amount: amount ?? 0 })),
  ]);

  return {
    lineAmounts,
    shippingAmounts,
    ...amounts,
    isComplete: [...lineAmounts, ...shippingAmounts].every((amount) => amount !== null),
  };
};
